import { EligibilityVerificationType } from '../enums';
import { Deserializable } from './deserializable.model';
import { JsonObject } from './json-object.model';
import { LiveSupport } from './live-support.model';
import { Report } from './report.model';
import { SubscriptionPlan } from './subscription-plan.model';
import { Theme } from './theme.model';
import { Url } from './url.model';

// NOTE: This file contains the classes for Organization and Group to avoid a Circular Dependency Warning
// as the classes both depend on each other

export class Organization implements Deserializable<JsonObject, Organization> {
  distributor;
  constructor(
    public id?: string,
    public name?: string,
    _distributor?: Organization,
    public groups?: Group[],
    public hasAccess?: boolean,
    // Reports data objects
    public reports?: Report[]
  ) {
    if (_distributor != null) {
      this.distributor = new Organization(_distributor.id, _distributor.name);
    }
  }

  /**
   * Assign Organization params from JSON
   */
  deserialize(json: JsonObject, existingData?: Organization): Organization {
    if (json == null) {
      return null;
    }

    this.id = json['id'] ?? existingData?.id;
    this.name = json['name'] || existingData?.name;
    const normalizedDistributor = json['distributor'] ?? existingData?.distributor;
    this.distributor = normalizedDistributor && new Organization().deserialize(normalizedDistributor);
    this.groups = (json['groups'] ?? existingData?.groups)?.map((group) => new Group().deserialize(group));
    this.hasAccess = json['hasAccess'];

    const reportsJson = json['reports'] ?? existingData?.reports;
    if (Object.keys(reportsJson || {}).length > 0) {
      this.reports = reportsJson?.map?.((report) => new Report().deserialize(report));
    }

    return this;
  }

  addReports(reports: Report[] | JsonObject) {
    if (Array.isArray(reports) && reports.every((report) => report instanceof Report)) {
      this.reports = reports;
    } else {
      this.reports = reports?.map?.((report) => new Report().deserialize(report));
    }
  }
}

export class Group implements Deserializable<JsonObject, Group> {
  constructor(
    public id?: string,
    public name?: string,
    public groupCode?: string,
    public organization?: Organization,
    public organizationName?: string,
    public dateCreated?: Date,
    public dateExpires?: Date,
    public eligibilityVerificationTypes?: EligibilityVerificationType[],
    public eligibilityFileHasFamilyMembers?: boolean,
    public liveSupport?: LiveSupport,
    public singleSignOn?: boolean,
    public totalLicenses?: number,
    public remainingLicenses?: number,
    public urls?: Url[],
    public subscriptionPlans?: SubscriptionPlan[],
    public theme?: Theme
  ) {}

  /**
   * Assign Group params from JSON
   */
  deserialize(json: JsonObject, existingData?: Group): Group {
    if (json == null) {
      return null;
    }

    this.id = json['id'] ?? existingData?.id;
    // checking for groupName for groupValidation responses that send name that way
    this.name = (json['name'] || json['groupName']) ?? (existingData?.name || existingData?.['groupName']);
    this.groupCode = json['groupCode'] ?? existingData?.groupCode;
    this.organization = new Organization().deserialize(
      { ...json['organization'], name: json['organizationName'] ?? json['organization']?.['name'] } ??
        existingData?.organization
    );
    this.organizationName = json['organizationName'] ?? existingData?.organizationName;
    if (json['dateCreated']) {
      this.dateCreated = new Date(json['dateCreated']) ?? existingData?.dateCreated;
    }
    if (json['dateExpires']) {
      this.dateExpires = new Date(json['dateExpires']) ?? existingData?.dateExpires;
    }
    this.eligibilityVerificationTypes = (json['eligibilityVerificationTypes'] ??
      existingData?.eligibilityVerificationTypes) as EligibilityVerificationType[];
    this.eligibilityFileHasFamilyMembers =
      json['eligibilityFileHasFamilyMembers'] ?? existingData?.eligibilityFileHasFamilyMembers;
    this.liveSupport = new LiveSupport().deserialize(json['liveSupport'] ?? existingData?.liveSupport);
    this.singleSignOn = json['singleSignOn'] ?? existingData?.singleSignOn;
    this.totalLicenses = json['totalLicenses'] ?? existingData?.totalLicenses;
    this.remainingLicenses = json['remainingLicenses'] ?? existingData?.remainingLicenses;
    this.urls = (json['urls'] ?? existingData?.urls)?.map((url) => new Url().deserialize(url));
    this.subscriptionPlans = (json['subscriptionPlans'] ?? existingData?.subscriptionPlans)?.map((plan) =>
      new SubscriptionPlan().deserialize(plan)
    );
    this.theme = new Theme().deserialize(json['theme'] ?? existingData?.theme);

    return this;
  }
}
